import {
  Autocomplete,
  Button,
  Divider,
  Drawer,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { UserGroup } from '@ulysses-inc/harami_api_client'
import { useFormik } from 'formik'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Yup from 'src/features/validation/yup'
import { updateTemplateNode } from 'src/state/ducks/templates/actions'
import { closeEditDeviateNotify } from 'src/state/ducks/templates/editDeviateNotify/actions'
import type { RootState } from 'src/state/store'

const validationSchema = Yup.object().shape({
  userGroups: Yup.array().label('ユーザーグループ'),
})

const EditDeviateNotifyDrawer: React.FC = () => {
  const dispatch = useDispatch()

  const {
    // 編集対象の質問ノード
    questionNode,
    isOpenModal: isOpen,
  } = useSelector((state: RootState) => state.templatesState.editDeviateNotify)

  const allUserGroups = useSelector(
    (state: RootState) => state.usersState.userGroups.userGroups || [],
  )

  const currentlyAssignedUserGroups =
    questionNode?.question?.deviateNotify?.userGroups ?? []

  const { values, handleSubmit, setFieldValue, resetForm } = useFormik({
    initialValues: {
      userGroups: currentlyAssignedUserGroups,
    },
    validationSchema,
    onSubmit: (values, formikHelpers) => {
      // 単に型の都合であり、実際にはundefinedになることはない想定
      if (questionNode === undefined) return
      dispatch(
        updateTemplateNode(questionNode.id, {
          ...questionNode,
          question: {
            ...questionNode.question,
            // `deviateNotify`の中に逸脱時通知先の情報が入っており、
            // その中には実のところuserGroupsというプロパティしかないので、このように書き換えればOKらしい。
            deviateNotify: values,
          },
        }),
      )
      dispatch(closeEditDeviateNotify())
      formikHelpers.resetForm()
    },
    enableReinitialize: true,
  })

  const handleClose = () => {
    resetForm()
    dispatch(closeEditDeviateNotify())
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        handleClose()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 600,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          逸脱時通知先の編集
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <Typography fontWeight="bold">ユーザーグループ</Typography>
          <Autocomplete
            multiple
            options={allUserGroups}
            getOptionLabel={option => option.name || ''}
            getOptionDisabled={option => !hasUsers(option)}
            value={values.userGroups ?? []}
            onChange={(_, newValue) => setFieldValue('userGroups', newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            slotProps={{ chip: { variant: 'pale', size: 'small' } }}
            renderInput={params => (
              <TextField
                {...params}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    'aria-label': 'ユーザーグループ',
                    'data-testid': 'select-deviate-notify-user-group',
                  },
                }}
              />
            )}
            renderOption={(props, option) => {
              // eslint-disable-next-line react/prop-types
              const { key, ...restProps } = props
              return (
                <li
                  key={key}
                  data-testid="select-deviate-notify-user-group-item"
                  {...restProps}
                >
                  {hasUsers(option)
                    ? option.name || ''
                    : `${option.name || ''} (ユーザーが紐づいていません)`}
                </li>
              )
            }}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={handleClose}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

function hasUsers(userGroup: UserGroup) {
  return (userGroup.users ?? []).length > 0
}

export default EditDeviateNotifyDrawer
